import React from 'react';
import noop from 'lodash/noop';
import {
  Box,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SwipeableDrawer,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';

import { useDictionary } from '@cvt/hooks/useDictionary';

import { isNative } from '@shared/helpers/environment';

import { CreateEvent } from '../partials/CreateEvent';

type Props = {
  open: boolean;
  onClose: () => void;
  data?: {
    onClose: (event?: Events.Event) => void;
    eventFormValidateFields?: Partial<{
      name: (name: string) => string | boolean;
    }>;
  }
}

export const CreateEventDialog: React.FC<Props> = ({ open, onClose, data }) => {
  const dictionary = useDictionary();

  const [visible, setVisible] = React.useState(true);

  const _onClose = React.useCallback((event: Events.Event) => {
    onClose();
    data?.onClose && data.onClose(event);
  }, [data, onClose]);

  React.useEffect(() => {
    setVisible(true);
  }, [open]);

  if (!visible) {
    return null;
  }

  return (
    <SwipeableDrawer
      disableSwipeToOpen
      anchor="bottom"
      open={open}
      onOpen={noop}
      onClose={onClose}
      PaperProps={{
        sx: theme => ({
          left: theme.spacing(1.5),
          right: theme.spacing(1.5),
          bottom: isNative() ? 'env(safe-area-inset-bottom)' : theme.spacing(1.5),
          borderRadius: theme.shape.borderRadius,
          maxWidth: theme.breakpoints.values.sm,
          maxHeight: `calc(100% - env(safe-area-inset-top) - ${isNative() ? 'env(safe-area-inset-bottom)' : theme.spacing(3)})`,
          margin: 'auto',
        }),
      }}
    >
      <DialogTitle component={Stack} direction="row" alignItems="center" justifyContent="space-between">
        <Box width={40}/>
        <Typography variant="h1">{dictionary.events.create.title}</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          color="secondary"
        >
          <Close/>
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box pt={1}>
          {/* Need it because SwipeableDrawer always mounts */}
          {
            open && <CreateEvent
              onSuccess={_onClose}
              validateFields={data?.eventFormValidateFields}
            />
          }
        </Box>
      </DialogContent>
    </SwipeableDrawer>
  );
};
